import { debounce, isEqual } from 'lodash-es';
import { GlIntersectionObserver, GlLoadingIcon } from '../../../../../node_modules/@gitlab/ui/src/index.js?vue3';
import { DEFAULT_DEBOUNCE_AND_THROTTLE_MS } from '~/lib/utils/constants';
import { glSlotsMixin } from '~/lib/utils/vue3compat/gl_slots_mixin';
import PageHeading from './page_heading.vue';
export default {
    name: 'BaseLayout',
    components: {
        GlIntersectionObserver,
        GlLoadingIcon,
        PageHeading
    },
    mixins: [
        glSlotsMixin
    ],
    SCROLL_CONTAINER_SELECTOR: '.panel-content-inner',
    inject: {
        // Provided by an ancestor DynamicPanel. Falls back to the gon default when
        // the layout is rendered outside a panel (for example in work items).
        // `fluidLayout` is static per panel/page, so a one-time injected value is enough.
        isFluidLayout: {
            from: 'fluidLayout',
            default: ()=>window.gon?.fluid_layout ?? false
        }
    },
    props: {
        heading: {
            type: String,
            required: false,
            default: null
        },
        headingTag: {
            type: String,
            required: false,
            default: null,
            validator: (value)=>value === null || [
                    'h1',
                    'h2'
                ].includes(value)
        },
        description: {
            type: String,
            required: false,
            default: null
        },
        pageHeadingSrOnly: {
            type: Boolean,
            required: false,
            default: false
        },
        // Skips the page heading entirely, for example when a surrounding modal
        // already provides the heading. Prefer `pageHeadingSrOnly` otherwise.
        hidePageHeading: {
            type: Boolean,
            required: false,
            default: false
        },
        loading: {
            type: Boolean,
            required: false,
            default: false
        },
        animateStickyHeader: {
            type: Boolean,
            required: false,
            default: true
        }
    },
    emits: [
        'sticky-change'
    ],
    data () {
        return {
            isStuck: false,
            observerOptions: null,
            // Bumped whenever observerOptions change, to remount the observer (via :key)
            // so it re-reads options, which GlIntersectionObserver only reads once.
            observerKey: 0
        };
    },
    watch: {
        isStuck: {
            handler (isStuck) {
                if (isStuck) {
                    this.$nextTick(()=>{
                        this.syncStickyHeaderHeight();
                        this.$emit('sticky-change', true);
                    });
                } else {
                    document.documentElement.style.removeProperty('--layout-sticky-header-height');
                    this.$emit('sticky-change', false);
                }
            }
        }
    },
    mounted () {
        if (this.glSlots()['sticky-header']) {
            // The sticky header's height (and thus rootMargin) can change after mount from
            // responsive breakpoints, web font load, or dynamic slot content. A
            // ResizeObserver on the header catches all of these, unlike a window resize.
            this.debouncedSyncObserverOptions = debounce(this.syncObserverOptions, DEFAULT_DEBOUNCE_AND_THROTTLE_MS);
            this.stickyHeaderResizeObserver = new ResizeObserver(this.debouncedSyncObserverOptions);
            this.observeStickyHeader();
            this.syncObserverOptions();
        }
    },
    beforeDestroy () {
        this.stickyHeaderResizeObserver?.disconnect();
        this.debouncedSyncObserverOptions?.cancel();
    },
    methods: {
        syncObserverOptions () {
            const root = this.$el.closest(this.$options.SCROLL_CONTAINER_SELECTOR);
            const options = {};
            // Null root falls back to the viewport, e.g. when not inside a panel.
            if (root) options.root = root;
            const rootMargin = this.stickyHeaderRootMargin();
            if (rootMargin) options.rootMargin = rootMargin;
            const nextOptions = Object.keys(options).length ? options : null;
            if (isEqual(nextOptions, this.observerOptions)) return;
            this.observerOptions = nextOptions;
            // Remounting the observer via :key recreates the sticky-header element, so
            // re-point the ResizeObserver at the new one once it has rendered.
            this.observerKey += 1;
            this.$nextTick(this.observeStickyHeader);
        },
        observeStickyHeader () {
            const el = this.$refs.stickyHeader;
            if (!this.stickyHeaderResizeObserver || !el) return;
            this.stickyHeaderResizeObserver.disconnect();
            this.stickyHeaderResizeObserver.observe(el);
        },
        stickyHeaderRootMargin () {
            const el = this.$refs.stickyHeader;
            if (!el) return null;
            // Sticky header height without its bottom padding spacer, so the header sticks
            // as the heading reaches where the sticky header's content bottom will sit.
            const { paddingBottom } = window.getComputedStyle(el);
            const offset = el.offsetHeight - (parseFloat(paddingBottom) || 0);
            if (offset <= 0) return null;
            // eslint-disable-next-line @gitlab/require-i18n-strings -- CSS rootMargin value, not user-facing
            return `-${offset}px 0px 0px 0px`;
        },
        syncStickyHeaderHeight () {
            const el = this.$refs.stickyHeader;
            if (!el) return;
            // Offsets content while the header is showing; removed when it hides.
            document.documentElement.style.setProperty('--layout-sticky-header-height', `${el.offsetHeight}px`);
        }
    }
};
